import type { ReactNode } from 'react'
import { ArrowUp, ArrowDown } from 'lucide-react'
import type { NwsObservation, NwsForecast, NwsPeriod, NwsPoint } from '@/lib/nws/types'
import { show } from '@/lib/nws/units'
import { useEaseTo } from '@/components/hooks/useEaseTo'
import { useConditions } from './useConditions'
import { useExtraConditions } from './useExtraConditions'
import { ConditionChips } from './ConditionChips'
import { WeatherGlyph } from '@/components/WeatherGlyph'

/**
 * Boxless hero: the day/night/condition glyph + the big cool-gradient temperature over
 * the globe, the condition word, tinted Hi/Lo, and the 4×2 conditions chips. No scrim and
 * no card — the globe stays full brightness; each glyph carries its own shadow for legibility.
 */
export function ConditionsHero({
  observation,
  forecast,
  currentPeriod,
  hi,
  lo,
  lat,
  lon,
  point,
  alert,
}: {
  observation: NwsObservation | null
  forecast: NwsForecast | undefined
  currentPeriod: NwsPeriod | undefined
  hi: number | null
  lo: number | null
  lat: number
  lon: number
  point: NwsPoint | undefined
  /** Alert bar rendered with the chips in the panel foot (full-width below / in column). */
  alert?: ReactNode
}) {
  const { conditionText, iconKey, temp, feels, feelsDiff, humidity, wind, windDir, pressure } =
    useConditions(observation, forecast, currentPeriod)
  const extra = useExtraConditions(lat, lon, point, observation)
  const shownTemp = useEaseTo(temp == null ? null : Math.round(temp))

  // The readout (icon + temp + condition + hi/lo) and the chips are SIBLINGS so the hero
  // panel can place them as separate groups — readout hugging the globe's curve, the chips
  // full-width below (mobile) or stacked in the right column (desktop).
  return (
    <>
      <div className="hero">
        <div className="hero__main">
          <WeatherGlyph code={iconKey} size={60} className="hero__glyph" />
          <div className="hero__temp">
            <span className="hero__num num">{show(shownTemp)}</span>
            {temp != null && <span className="hero__deg">°</span>}
          </div>
        </div>

        <p className="hero__condition">{conditionText}</p>

        <div className="hero__hilo">
          {hi != null && (
            <span className="hero__hi">
              <ArrowUp size={15} />
              <span className="num">{Math.round(hi)}°</span>
            </span>
          )}
          {lo != null && (
            <span className="hero__lo">
              <ArrowDown size={15} />
              <span className="num">{Math.round(lo)}°</span>
            </span>
          )}
        </div>
      </div>

      <div className="home-hero__foot">
        <ConditionChips
          feels={feels}
          feelsDiff={feelsDiff}
          humidity={humidity}
          dewPointF={extra.dewPointF}
          wind={wind}
          windDir={windDir}
          pressure={pressure}
          pressureTrend={extra.pressureTrend}
          aqi={extra.aqi}
          uv={extra.uv}
          visibilityMi={extra.visibilityMi}
          spc={extra.spc}
        />
        {alert}
      </div>
    </>
  )
}
